/**
 * Reusable analytics card component for displaying metrics.
 */

import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';

interface AnalyticsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  icon?: React.ReactNode;
  className?: string;
  loading?: boolean;
}

const AnalyticsCard: React.FC<AnalyticsCardProps> = ({
  title,
  value,
  subtitle,
  trend,
  trendValue,
  icon,
  className = '',
  loading = false
}) => {
  const { theme } = useAppSelector((state) => state.ui);

  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      case 'neutral':
        return <Minus className="w-4 h-4 text-gray-500" />;
      default:
        return null;
    }
  };

  const getTrendColor = () => {
    switch (trend) {
      case 'up':
        return 'text-green-500';
      case 'down':
        return 'text-red-500';
      case 'neutral':
        return 'text-gray-500';
      default:
        return 'text-gray-500';
    }
  };

  if (loading) {
    return (
      <div className={`relative w-full overflow-hidden rounded-lg border transition-all duration-300 ${theme === 'dark' ? 'bg-gray-900/60 border-gray-700/60' : 'bg-white/80 border-gray-200'} ${className}`}>
        <div className="flex items-center gap-3 px-3 py-2.5 sm:px-4 sm:py-3">
          <div className={`flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
          <div className="flex-1 min-w-0 space-y-1">
            <div className={`h-3 rounded w-20 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
            <div className={`h-5 rounded w-12 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative w-full overflow-hidden rounded-lg border transition-all duration-300 hover:shadow-md ${theme === 'dark' ? 'bg-gray-900/60 border-gray-700/60 hover:border-gray-600' : 'bg-white/80 border-gray-200 hover:border-gray-300'} ${className}`}>
      <div className="relative z-10 flex items-center gap-3 px-3 py-2.5 sm:px-4 sm:py-3">
        {icon && (
          <div className={`flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            {icon}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-medium truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            {title}
          </p>
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className={`text-base sm:text-lg font-semibold tracking-tight truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {value}
            </p>
            {trend && trendValue && (
              <span className={`flex items-center gap-0.5 text-xs font-medium ${getTrendColor()}`}>
                {getTrendIcon()}
                {trendValue}
              </span>
            )}
          </div>
          {subtitle && (
            <p className={`text-xs mt-0.5 truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsCard;
